import { useState, useEffect } from 'react'
import api from '../../lib/api'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { useBrand } from '@/hooks/useBrand'
import {
  Phone,
  MessageSquare,
  Brain,
  BarChart3,
  ChevronRight,
  Loader2,
  Calendar,
  ClipboardCheck,
  Mic,
  Users,
} from 'lucide-react'
import { motion } from 'framer-motion'
import confetti from 'canvas-confetti'

interface CompleteStepProps {
  onComplete: () => void
  onBack?: () => void
}

export default function CompleteStep({ onComplete, onBack }: CompleteStepProps) {
  const [isActivating, setIsActivating] = useState(false)
  const { toast } = useToast()
  const brand = useBrand()
  const supportEmail = brand.supportEmail ?? 'support@legallady.ai'

  useEffect(() => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#4d71fd', '#7a9df8', '#bbd9ff', '#192480', '#eef2ff']
    })
  }, [])

  const handleActivate = async () => {
    setIsActivating(true)
    try {
      const response = await api.post('/api/onboarding/complete')

      if (response.data.success) {
        if (response.data.token) {
          localStorage.setItem('app_auth_token', response.data.token)
          api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`
        }

        localStorage.setItem('app_onboarding_complete', 'true')

        toast({
          title: 'Welcome aboard!',
          description: 'Your live intake coverage starts now. Let\'s create your first AI agent.'
        })

        confetti({
          particleCount: 200,
          spread: 100,
          origin: { y: 0.4 },
          colors: ['#4d71fd', '#7a9df8', '#bbd9ff', '#192480', '#eef2ff']
        })

        setTimeout(() => {
          onComplete()
        }, 1000)
      }
    } catch (error: any) {
      toast({
        title: 'Activation failed',
        description: error.response?.data?.message || 'Something went wrong',
        variant: 'destructive'
      })
    } finally {
      setIsActivating(false)
    }
  }

  const highlights = [
    {
      icon: Phone,
      label: 'AI Voice Agents',
      detail: 'Inbound & outbound calls, answered 24/7',
    },
    {
      icon: MessageSquare,
      label: 'AI Chat Widget',
      detail: 'Engage website visitors in real time',
    },
    {
      icon: ClipboardCheck,
      label: 'Smart Lead Capture',
      detail: 'Auto-qualify prospects before your team sees them',
    },
    {
      icon: Brain,
      label: 'Knowledge Base',
      detail: 'Firm-specific answers pulled from your site',
    },
    {
      icon: Mic,
      label: 'Call Transcripts',
      detail: 'Every call fully transcribed and searchable',
    },
    {
      icon: Calendar,
      label: 'Booking Detection',
      detail: 'Consultations get logged automatically',
    },
    {
      icon: Users,
      label: 'Team Messaging',
      detail: 'Chat, assign leads, handoff between agents',
    },
    {
      icon: BarChart3,
      label: 'Analytics & Reporting',
      detail: 'See what\u2019s working, down to the call',
    },
  ]

  return (
    <div className="max-w-2xl lg:max-w-5xl mx-auto px-4 sm:px-0">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
      >
        {/* Hero — big confident headline, no celebratory pills */}
        <div className="mb-8 lg:mb-10 text-center lg:text-left max-w-2xl lg:max-w-none">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-periwinkle/80 mb-3">
            Done &mdash; You're in
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-heading font-semibold text-white tracking-tight leading-[1.05]">
            Your firm is wired up.<br className="hidden sm:inline" />
            <span className="text-white/60">Let's put it to work.</span>
          </h2>
          <p className="text-white/60 mt-4 text-[15px] leading-relaxed max-w-lg mx-auto lg:mx-0">
            We've analyzed your website, built your knowledge base, and your
            account is ready. One last step &mdash; create your first AI agent.
          </p>
        </div>

        {/* What's unlocked — gallery-style, numbered, no card-in-card soup */}
        <div className="mb-10">
          <div className="flex items-baseline justify-between mb-4 gap-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/50">
              What's now unlocked
            </p>
            <span className="text-[11px] text-white/35 shrink-0">Live intake coverage active</span>
          </div>
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-5 border-t border-white/8 pt-5"
            initial="hidden"
            animate="show"
            variants={{
              hidden: {},
              show: { transition: { staggerChildren: 0.05, delayChildren: 0.15 } },
            }}
          >
            {highlights.map((item, idx) => (
              <motion.div
                key={item.label}
                variants={{
                  hidden: { opacity: 0, y: 12 },
                  show: { opacity: 1, y: 0, transition: { ease: [0.23, 1, 0.32, 1], duration: 0.5 } },
                }}
                className="text-left"
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-[10px] font-mono text-white/30 tabular-nums">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <item.icon className="h-3.5 w-3.5 text-brand-periwinkle/80" strokeWidth={1.75} />
                </div>
                <p className="text-sm font-medium text-white leading-tight">{item.label}</p>
                <p className="text-xs text-white/50 leading-relaxed mt-1">{item.detail}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* CTA row */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 max-w-xl">
          <Button
            size="lg"
            className="h-12 px-6 rounded-xl text-[15px] bg-gradient-to-r from-brand-royal to-brand-periwinkle hover:brightness-110 text-white font-semibold shadow-lg shadow-brand-royal/30 hover:shadow-brand-royal/50 transition-[transform,box-shadow,filter] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] disabled:active:scale-100 border-0 inline-flex items-center gap-2"
            onClick={handleActivate}
            disabled={isActivating}
          >
            {isActivating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Activating…
              </>
            ) : (
              <>
                Create your first AI agent
                <ChevronRight className="h-4 w-4" strokeWidth={2} />
              </>
            )}
          </Button>

          {onBack && (
            <Button
              variant="ghost"
              onClick={onBack}
              className="!text-white/55 hover:!text-white hover:!bg-white/5 text-sm font-medium h-12 px-5 transition-colors duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.98]"
            >
              Go back
            </Button>
          )}
        </div>

        <p className="text-xs text-white/30 mt-6 text-center lg:text-left">
          Stuck?{' '}
          <a
            href={`mailto:${supportEmail}`}
            className="text-brand-periwinkle/70 hover:text-white transition-colors duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]"
          >
            {supportEmail}
          </a>
        </p>
      </motion.div>
    </div>
  )
}
